// client/src/lib/tradePresetStore.ts
//
// localStorage-backed store for user-saved search-string presets. Built-in
// presets were removed from the Search page — the only presets now are the
// ones the user saves. The most-recently-saved (or most-recently-loaded)
// preset is remembered as the "default" so the page re-opens with it applied.
//
// Mirrors the customDexLinkagePref / dexVisibilityStore pattern: a single JSON
// key, an in-memory cache, and a change event for cross-component sync.

import { defaultConfig, type TradeStringConfig } from "@/lib/tradeString";

const STORAGE_KEY = "nexus.tradePresets.v1";

export const TRADE_PRESET_EVENT = "nexus:trade-presets-changed";

export interface SavedTradePreset {
  id: string;
  name: string;
  // Full config snapshot. `source` / `dexNumbers` / `dexMode` are stored for
  // completeness but are overridden on load with the page's current dex, so a
  // preset only really carries filters + tags + custom operators.
  config: TradeStringConfig;
}

interface PresetStoreShape {
  presets: SavedTradePreset[];
  defaultId: string | null;
}

let cache: PresetStoreShape | null = null;

function empty(): PresetStoreShape {
  return { presets: [], defaultId: null };
}

function load(): PresetStoreShape {
  if (cache !== null) return cache;
  if (typeof window === "undefined") {
    cache = empty();
    return cache;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      cache = empty();
      return cache;
    }
    const parsed = JSON.parse(raw) as Partial<PresetStoreShape>;
    cache = {
      presets: Array.isArray(parsed.presets) ? parsed.presets : [],
      defaultId: typeof parsed.defaultId === "string" ? parsed.defaultId : null,
    };
    return cache;
  } catch {
    cache = empty();
    return cache;
  }
}

function persist(next: PresetStoreShape): void {
  cache = next;
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // ignore quota errors
    }
    window.dispatchEvent(new CustomEvent(TRADE_PRESET_EVENT));
  }
}

export function listTradePresets(): SavedTradePreset[] {
  return load().presets;
}

export function getDefaultTradePresetId(): string | null {
  return load().defaultId;
}

export function getDefaultTradePreset(): SavedTradePreset | null {
  const { presets, defaultId } = load();
  if (!defaultId) return null;
  return presets.find((p) => p.id === defaultId) ?? null;
}

/**
 * Save a new preset from the given config. The new preset becomes the default,
 * so the page re-opens with it next time. Returns the created preset.
 */
export function saveTradePreset(name: string, config: TradeStringConfig): SavedTradePreset {
  const { presets } = load();
  const preset: SavedTradePreset = {
    // Date.now/Math.random are fine in app code (this store never runs inside a
    // workflow script); combine both to avoid same-ms collisions.
    id: `preset-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
    name: name.trim(),
    config: JSON.parse(JSON.stringify(config)) as TradeStringConfig,
  };
  persist({ presets: [...presets, preset], defaultId: preset.id });
  return preset;
}

/** Mark an existing preset as the default (the one the page opens with). */
export function setDefaultTradePreset(id: string | null): void {
  const { presets } = load();
  persist({ presets, defaultId: id });
}

/** Delete a preset. If it was the default, the default clears. */
export function deleteTradePreset(id: string): void {
  const { presets, defaultId } = load();
  persist({
    presets: presets.filter((p) => p.id !== id),
    defaultId: defaultId === id ? null : defaultId,
  });
}

/** Normalise a tag the way the Search page's own add box does. */
function normalizeTag(raw: string): string | null {
  const t = raw.trim();
  if (!t) return null;
  return t.startsWith("#") ? t : `#${t}`;
}

/** True when the tag is already in the default preset's "Your tags" list. */
export function isTagInSearch(raw: string): boolean {
  const tag = normalizeTag(raw);
  if (!tag) return false;
  const def = getDefaultTradePreset();
  if (!def) return false;
  return def.config.tags.some((t) => t.tag.toLowerCase() === tag.toLowerCase());
}

/**
 * Add a tag to the Search page's "Your tags" from anywhere in the app (the
 * My Pokémon detail page's "Add to search tags" button). Writes into the
 * DEFAULT preset — the one the Search page opens with — creating a starter
 * preset when none exists yet, so the button always lands somewhere the user
 * will actually see. New tags arrive as "exclude", matching the Search page's
 * own add box; the user flips the tri-state there.
 */
export function addTagToSearch(raw: string): "added" | "exists" | "invalid" {
  const tag = normalizeTag(raw);
  if (!tag) return "invalid";
  const { presets, defaultId } = load();
  const def = presets.find((p) => p.id === defaultId) ?? null;
  if (!def) {
    const config = defaultConfig([], "main");
    saveTradePreset("My search", { ...config, tags: [...config.tags, { tag, state: "exclude" }] });
    return "added";
  }
  if (def.config.tags.some((t) => t.tag.toLowerCase() === tag.toLowerCase())) return "exists";
  const next = presets.map((p) =>
    p.id === def.id
      ? { ...p, config: { ...p.config, tags: [...p.config.tags, { tag, state: "exclude" as const }] } }
      : p,
  );
  persist({ presets: next, defaultId });
  return "added";
}

/** Test-only helper: reset the in-memory cache. */
export function __resetTradePresetCacheForTests(): void {
  cache = null;
}
